import {Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {
    faChevronRight,
    faExternalLink,
    faExclamationTriangle,
    faFilePdf,
    faImage,
    faPaintBrush,
    faSpeaker,
    faSyncAlt, faTimes, faTv, faVideo
} from '@fortawesome/pro-solid-svg-icons';
import {HydratedAuditTarget} from '@webmate-web-core/shared/services/audit.service';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';

import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {WmDirectivesModule} from '@webmate-web-core/shared/directives/directives.module';
import {StateModule} from '@webmate-web-core/shared/components/state/state.module';
import {ResourceViewsModule} from '@webmate-web-core/shared/components/resources/resource-views.module';
import {ButtonModule} from 'primeng/button';
import {TagsModule} from '@webmate-web-core/shared/components/tags/tags.module';
import {ScreenshotModule} from '@webmate-web-core/shared/components/screenshot/screenshot.module';
import {TooltipModule} from 'primeng/tooltip';
import {FactsModule} from '@webmate-web-core/shared/components/facts/facts.module';
import {BrowserSessionService} from '@webmate-web-core/shared/services/browsersession.service';
import {AutomationPolicyConfig, State} from '@webmate-web-core/shared/types/browsersession';
import {Artifact} from '@webmate-web-core/shared/types/artifact';
import {AuditId, TestObjectId} from '@webmate-web-core/shared/types/ids';
import {WmDateFormatPipe} from '@webmate-web-core/shared/utils/pipes/wm-date-format.pipe';
import {FormsModule} from '@angular/forms';
import {InputTextModule} from 'primeng/inputtext';
import _ from 'lodash';
import {Textarea} from 'primeng/textarea';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ImageService} from '@webmate-web-core/shared/services/image.service';
import {ClippingWindowComponent} from '@webmate-web-core/misc/clipping-window/clipping-window.component';
import {JsonPipe} from '@angular/common';
import {RouterLink} from '@angular/router';
import {ProgressBarModule} from 'primeng/progressbar';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {Progress} from '@webmate-web-core/shared/types/base-types';
import {StateCollectorJob, TestObject} from '@webmate-web-core/shared/types/testmgmt';
import {
    getTestObjectOriginDescription,
    getTestObjectTypeLabel
} from '@webmate-web-core/shared/components/testmgmt/splitview/testobject-overview.utils';
import {Tag, tagToString} from '@webmate-web-core/shared/types/tag';
import {TestObjectTypeIconComponent} from '@webmate-web-core/shared/components/testmgmt/splitview/testobject-type-icon.component';
import {
    extractFirstUrl,
    getInteractionPathLastUrl,
    getInteractionPathStepCount,
    getStateDisplayLabel
} from '@webmate-web-core/shared/utils/interaction-path-display.util';
import {
    cloneAutomationPolicy,
    normalizeAutomationPolicy
} from '@webmate-web-core/shared/utils/testobject-automation-policy.util';
import {
    TESTOBJECT_EXPEDITION_OPENER,
    TestObjectExpeditionOpener
} from '@webmate-web-core/shared/services/testobject-expedition-opener.service';

export interface TestObjectDetailUpdate {
    testObject: HydratedAuditTarget;
    automationPolicy?: AutomationPolicyConfig | null;
    automationPolicyChanged: boolean;
}


@UntilDestroy()
@Component({
    selector: 'wm-test-object-detail',
    templateUrl: './test-object-detail.component.html',
    styleUrls: ['./test-object-detail.component.less'],
    imports: [FontAwesomeModule, WmDirectivesModule, StateModule, ResourceViewsModule, ButtonModule, TagsModule, ScreenshotModule, TooltipModule, FactsModule, WmDateFormatPipe, FormsModule, InputTextModule, Textarea, ClippingWindowComponent, ProgressBarModule, ProgressSpinnerModule, TestObjectTypeIconComponent, RouterLink],
    standalone: true
})
export class TestObjectDetailComponent implements OnChanges {
    @Input() selectedTestObject: HydratedAuditTarget;
    @Input() testObjectsById: { [testObjectId: string]: HydratedAuditTarget } = {};
    @Input() canonicalTestObject?: TestObject;
    @Input() representativeStateId?: string;
    @Input() captureJob?: StateCollectorJob;
    @Input() readOnly: boolean = false;
    @Input() projectId: string;
    @Input() auditId?: AuditId;
    @Input() closable: boolean = true;
    @Input() stateCaptureAvailable: boolean = false;
    @Input() stateCaptureUnavailableReason?: string;

    @Output() onDeleteTestObject: EventEmitter<TestObjectId> = new EventEmitter();
    @Output() onRescanTestObject: EventEmitter<TestObjectId> = new EventEmitter();
    @Output() onUpdateTestObject: EventEmitter<TestObjectDetailUpdate> = new EventEmitter();
    @Output() onViewState: EventEmitter<any> = new EventEmitter();
    @Output() onSetTags: EventEmitter<Tag[]> = new EventEmitter();
    @Output() onCaptureRepresentativeState: EventEmitter<TestObjectId> = new EventEmitter();
    @Output() onClose: EventEmitter<void> = new EventEmitter;

    selectedObjectStateArtifacts: { [artifactType: string]: Artifact[] } = {};
    selectedObjectState?: State;
    tags?: Tag[] = [];
    imageUrl?: string;

    destroyCurrentState$ = new Subject<void>();

    editMode: boolean = false;
    selectedTestObjectBeforeEditCopy: HydratedAuditTarget;
    automationPolicyDraft?: AutomationPolicyConfig;
    automationPolicyOriginal?: AutomationPolicyConfig;

    constructor(
        private readonly expeditionService: BrowserSessionService,
        private readonly imageService: ImageService,
        @Inject(TESTOBJECT_EXPEDITION_OPENER) private readonly testObjectExpeditionOpener: TestObjectExpeditionOpener
    ) {
    }

    ngOnChanges(changes: SimpleChanges) {
        this.destroyCurrentState$.next();
        const relevantInputsChanged = ['selectedTestObject', 'canonicalTestObject', 'representativeStateId']
            .some(key => key in changes);

        if (relevantInputsChanged && this.selectedTestObject) {
            this.updateDetailInfo();
        }

        if ((changes['selectedTestObject'] || changes['canonicalTestObject']) && this.selectedTestObject) {
            this.resetAutomationPolicyDraft();
        }
    }

    getName(): string {
        if (!!this.selectedTestObject?.target?.name) {
            return this.selectedTestObject.target.name;
        } else if (!!this.canonicalTestObject?.name) {
            return this.canonicalTestObject.name;
        } else if (this.selectedObjectState) {
            return getStateDisplayLabel(this.selectedObjectState.description, this.selectedObjectState.provenance?.path)
                || this.selectedObjectState.description;
        } else {
            return 'Unnamed Test Object';
        }
    }

    editTestObject(testObjectId: TestObjectId) {
        this.selectedTestObjectBeforeEditCopy = _.cloneDeep(this.selectedTestObject);
        this.resetAutomationPolicyDraft();
        this.editMode = true;
    }

    saveChanges() {
        this.onUpdateTestObject.emit({
            testObject: this.selectedTestObject,
            automationPolicy: normalizeAutomationPolicy(this.automationPolicyDraft) || null,
            automationPolicyChanged: this.isAutomationPolicyDirty(),
        });
        this.automationPolicyOriginal = cloneAutomationPolicy(this.automationPolicyDraft);
        this.editMode = false;
    }

    discardChanges() {
        this.editMode = false;
        this.selectedTestObject = _.cloneDeep(this.selectedTestObjectBeforeEditCopy);
        this.resetAutomationPolicyDraft();
    }

    removeTestObject(testObjectId: TestObjectId) {
        this.onDeleteTestObject.emit(testObjectId);
    }

    viewState(state: any) {
        this.onViewState.emit(state);
    }

    setTagsForSelectedTestObject(tags: Tag[]) {
        this.onSetTags.emit(tags);
    }

    captureRepresentativeState(): void {
        if (this.selectedTestObject?.testObjectId) {
            this.onCaptureRepresentativeState.emit(this.selectedTestObject.testObjectId);
        }
    }

    protected getDescription(): string | undefined {
        return this.selectedTestObject?.target?.description || this.canonicalTestObject?.description;
    }

    protected getLabelStrings(): string[] {
        return (this.tags || [])
            .map(tag => tagToString(tag))
            .filter(label => !!label);
    }

    protected shouldShowLabelsSection(): boolean {
        return !this.readOnly || this.getLabelStrings().length > 0;
    }

    protected isAutomationPolicyDirty(): boolean {
        return !_.isEqual(
            normalizeAutomationPolicy(this.automationPolicyOriginal) || null,
            normalizeAutomationPolicy(this.automationPolicyDraft) || null
        );
    }

    protected getCanonicalTestObjectDetailLink(): unknown[] {
        return ['/projects', this.projectId, 'testlab', 'testobjects'];
    }

    protected getCanonicalTestObjectDetailQueryParams(): {selectedTestObjectId: TestObjectId} | undefined {
        const testObjectId = this.canonicalTestObject?.id || this.selectedTestObject?.testObjectId;
        return testObjectId ? {selectedTestObjectId: testObjectId as TestObjectId} : undefined;
    }

    protected hasSourceDetails(): boolean {
        return !!this.canonicalTestObject;
    }

    private updateDetailInfo() {
        if (!this.selectedTestObject) {
            return;
        }

        this.selectedObjectState = undefined;
        this.selectedObjectStateArtifacts = {};
        this.imageUrl = undefined;

        const testObject = this.testObjectsById[this.selectedTestObject.testObjectId] || this.selectedTestObject;
        this.tags = [...(this.canonicalTestObject?.labels || testObject?.target?.labels || [])];
        if (testObject) {
            const testObjectId = testObject.testObjectId || testObject.target?.testObjectId;
            if (testObjectId && this.representativeStateId) {
                this.expeditionService.getState$(this.representativeStateId)
                    .pipe(takeUntil(this.destroyCurrentState$), untilDestroyed(this))
                    .subscribe(state => {
                        if (!state) {
                            return;
                        }
                        this.selectedObjectState = state;
                        this.selectedObjectStateArtifacts = state.associatedArtifacts.reduce((acc: any, artifact) => {
                            if (!artifact) {
                                return acc;
                            }
                            if (!acc[artifact.artifactType]) {
                                acc[artifact.artifactType] = [];
                            }
                            acc[artifact.artifactType].push(artifact);
                            return acc;
                        }, {});
                    });
            } else if (!!testObject.target.screenshot) {
                if (!!testObject.target.screenshot) {
                    this.imageUrl = this.imageService.getImageUrl(testObject.target.screenshot);
                }
            }
        }
    }

    private resetAutomationPolicyDraft(): void {
        this.automationPolicyOriginal = cloneAutomationPolicy(this.canonicalTestObject?.automationPolicy);
        this.automationPolicyDraft = cloneAutomationPolicy(this.canonicalTestObject?.automationPolicy);
    }

    openInWorkbench() {
        const testObjectId = this.canonicalTestObject?.id || this.selectedTestObject?.testObjectId;
        if (testObjectId) {
            this.testObjectExpeditionOpener.open({
                testObjectId,
                projectId: this.projectId as any,
                auditId: this.auditId,
            });
        }
    }

    protected getOriginLabel(): string {
        return getTestObjectTypeLabel(this.canonicalTestObject?.origin);
    }

    protected getOriginDescription(): string {
        return getTestObjectOriginDescription(this.canonicalTestObject?.origin);
    }

    protected getGenerationLabel(): string {
        const generationSpec = this.canonicalTestObject?.generationSpec as any;
        switch (generationSpec?.type) {
            case 'URLGenerationSpec':
                return 'URL-based target';
            case 'InteractionPathGenerationSpec':
                return 'Interaction path target';
            case 'MobileAppGenerationSpec':
                return 'Mobile application';
            case 'ManualGenerationSpec':
                return 'Manual target';
            default:
                return generationSpec?.type || 'Unknown generation';
        }
    }

    protected getGenerationDescription(): string {
        const generationSpec = this.canonicalTestObject?.generationSpec as any;
        switch (generationSpec?.type) {
            case 'URLGenerationSpec':
                return generationSpec?.url || 'No URL recorded';
            case 'InteractionPathGenerationSpec':
                return generationSpec?.interactionPath?.steps?.length
                    ? `${generationSpec.interactionPath.steps.length} interaction step(s)`
                    : 'Interaction path recorded';
            case 'MobileAppGenerationSpec':
                return generationSpec?.packageId ? `Pinned package ${generationSpec.packageId}` : 'No mobile package recorded';
            case 'ManualGenerationSpec':
                return generationSpec?.instructions || 'Manual navigation required';
            default:
                return 'No generation details recorded';
        }
    }

    protected hasNoCapturedState(): boolean {
        return !this.selectedObjectState;
    }

    protected shouldShowLatestStateSection(): boolean {
        const generationType = (this.canonicalTestObject?.generationSpec as any)?.type;
        return !!this.selectedObjectState || generationType !== 'MobileAppGenerationSpec';
    }

    protected hasReferenceImage(): boolean {
        return !this.selectedObjectState && !!this.imageUrl;
    }

    protected hasStatePreview(): boolean {
        return !!(this.selectedObjectState?.firstScreenshotUrl || this.selectedObjectState?.firstScreenshotId);
    }

    protected canOpenStateInWorkbench(): boolean {
        return !!(this.canonicalTestObject?.id || this.selectedTestObject?.testObjectId) && this.hasExecutableGenerationSpec();
    }

    private hasExecutableGenerationSpec(): boolean {
        const generationType = (this.canonicalTestObject?.generationSpec as any)?.type;
        return generationType === 'URLGenerationSpec' || generationType === 'InteractionPathGenerationSpec';
    }

    protected getLatestStatePrimaryLabel(): string {
        return this.getLatestStateUrl()
            || this.getLatestStateFriendlyDescription()
            || this.canonicalTestObject?.name
            || this.selectedTestObject?.target?.name
            || 'Recorded state';
    }

    protected getLatestStateSecondaryLabel(): string | undefined {
        const latestStateUrl = this.getLatestStateUrl();
        const friendlyDescription = this.getLatestStateFriendlyDescription();
        const primaryLabel = this.getLatestStatePrimaryLabel();

        if (friendlyDescription && friendlyDescription !== primaryLabel && !this.isRedundantStateDescription(friendlyDescription, latestStateUrl)) {
            return friendlyDescription;
        }

        if (latestStateUrl && latestStateUrl !== primaryLabel) {
            return latestStateUrl;
        }

        return undefined;
    }

    protected getLatestStateStepCount(): number | undefined {
        return getInteractionPathStepCount(this.selectedObjectState?.provenance?.path);
    }

    protected hasStateMediaSummary(): boolean {
        return this.getStateMediaSummaryItems().length > 0;
    }

    protected getStateMediaSummaryItems(): Array<{ icon: any; label: string; className: string }> {
        const mediaInfo = this.getStateMediaInfo();
        if (!mediaInfo) {
            return [];
        }

        const summaryEntries = [
            {count: mediaInfo?.pdfs?.length ?? 0, icon: faFilePdf, label: 'PDFs', className: 'pdf'},
            {count: mediaInfo?.images?.length ?? 0, icon: faImage, label: 'Images', className: 'image'},
            {count: mediaInfo?.videos?.length ?? 0, icon: faVideo, label: 'Videos', className: 'video'},
            {count: mediaInfo?.audios?.length ?? 0, icon: faSpeaker, label: 'Audio', className: 'audio'},
            {count: mediaInfo?.svgs?.length ?? 0, icon: faImage, label: 'SVGs', className: 'svg'},
            {count: mediaInfo?.canvases?.length ?? 0, icon: faPaintBrush, label: 'Canvases', className: 'canvases'},
        ];

        return summaryEntries
            .filter(entry => entry.count > 0)
            .map(entry => ({
                icon: entry.icon,
                label: `${entry.count} ${entry.label}`,
                className: entry.className
            }));
    }

    protected hasAccessibilityReport(): boolean {
        return (this.selectedObjectStateArtifacts['Page.A11yReport'] || []).length > 0;
    }

    protected getA11yViolationCount(): number {
        const reportArtifact = (this.selectedObjectStateArtifacts['Page.A11yReport'] || [])[0];
        return reportArtifact?.['data']?.['reportData']?.['axeResults']?.['violations']?.length || 0;
    }

    protected getA11ySummaryText(): string {
        const violations = this.getA11yViolationCount();
        return violations > 0 ? `${violations} violation${violations === 1 ? '' : 's'}` : 'No violations';
    }

    protected isCaptureRunning(): boolean {
        return this.captureJob?.status === 'running';
    }

    protected isCaptureFailed(): boolean {
        return this.captureJob?.status === 'failed';
    }

    protected getCaptureProgressPercent(): number {
        const rawProgress = (this.captureJob?.progress as any)?.progress;
        return Math.round(this.getProgressFraction(rawProgress) * 100);
    }

    protected getCaptureProgressMessage(): string {
        if (this.captureJob?.progress?.failureMsg) {
            return this.captureJob.progress.failureMsg;
        }
        return this.captureJob?.progress?.progressMessage || 'Generating state snapshot...';
    }

    protected shouldShowCaptureAction(): boolean {
        return !this.readOnly && this.stateCaptureAvailable;
    }

    protected shouldShowUnavailableCaptureHint(): boolean {
        return !this.readOnly && !this.stateCaptureAvailable && !!this.stateCaptureUnavailableReason;
    }

    private getProgressFraction(progress?: Progress | number): number {
        if (typeof progress === 'number') {
            return Math.max(0, Math.min(1, progress));
        }
        const current = progress?.current ?? 0;
        return Math.max(0, Math.min(1, current));
    }

    private getStateMediaInfo(): any {
        return (this.selectedObjectStateArtifacts['Page.MediaInfo'] || [])
            .map(artifact => artifact?.['data']?.mediaInfo)
            .find(mediaInfo => !!mediaInfo);
    }

    private getLatestStateFriendlyDescription(): string | undefined {
        return getStateDisplayLabel(this.selectedObjectState?.description, this.selectedObjectState?.provenance?.path);
    }

    private getLatestStateUrl(): string | undefined {
        const candidates = [
            getInteractionPathLastUrl(this.selectedObjectState?.provenance?.path),
            this.getGenerationSpecUrl(),
            extractFirstUrl(this.selectedObjectState?.description),
            extractFirstUrl(this.canonicalTestObject?.name),
            extractFirstUrl(this.selectedTestObject?.target?.name),
        ];

        return candidates.find(candidate => !!candidate);
    }

    private getGenerationSpecUrl(): string | undefined {
        const generationSpec = this.canonicalTestObject?.generationSpec as any;
        if (typeof generationSpec?.url === 'string' && generationSpec.url.trim().length > 0) {
            return generationSpec.url.trim();
        }

        return undefined;
    }

    private isRedundantStateDescription(description: string, url?: string): boolean {
        return !!url && (description === url || description === `Navigate to ${url}`);
    }

    protected readonly faFilePdf = faFilePdf;
    protected readonly faImage = faImage;
    protected readonly faSpeaker = faSpeaker;
    protected readonly faChevronRight = faChevronRight;
    protected readonly faExclamationTriangle = faExclamationTriangle;
    protected readonly faPaintBrush = faPaintBrush;
    protected readonly faSyncAlt = faSyncAlt;
    protected readonly faVideo = faVideo;
    protected readonly faTimes = faTimes;
    protected readonly faTv = faTv;
    protected readonly faExternalLink = faExternalLink;
}
